import "aframe";
import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { rotateY } from "./utils";

// Custom hooks
import {
  FOV,
  MAX_FOV,
  MIN_FOV,
  useCameraSync,
  useDrawObject,
  useFOVManagement,
  useImageLoading,
  useSceneInteraction,
  useZoomHandlers,
} from "./hooks";

// Components
import LoadingOverlay from "./LoadingOverlay";
import NavigationArrows from "./NavigationArrows";
import PanoramaControls from "./PanoramaControls";
import PanoramaScene from "./PanoramaScene";

// Types
import {
  AFrameCamera,
  AFrameScene,
  AFrameSky,
  BBox2D,
  CameraPosition,
  NavigationDirection,
} from "./types";

import { useAuth } from "contexts/AuthContext";
import usePrevious from "hooks/usePrevious";
import { IMAGE_STATUS } from "../../../common/constants";

interface PanoramaViewerProps {
  onNavigate?: (direction: NavigationDirection) => void;
  canNavigateForward?: boolean;
  canNavigateBackward?: boolean;
  canNavigateLeft?: boolean;
  canNavigateRight?: boolean;
  navigationDisabled?: boolean;
  cameraPositions?: CameraPosition[];
  currentFrameIndex?: number;
  onFrameChange?: ((frameIndex: number) => void) | null;
  debugCameraPositions?: boolean;
  currentSrc?: string | null;
  isLVMode?: boolean;
  currentImageStatus?: IMAGE_STATUS;
  setCurrentImageStatus: Dispatch<SetStateAction<IMAGE_STATUS>>;
  retryCurrentImage?: (() => void) | null;
  activeObjectId?: string | null;

  comparedCameraPositions?: CameraPosition[];
  comparedCurrentSrc?: string | null;
  comparedCurrentImageStatus?: IMAGE_STATUS;
  setComparedCurrentImageStatus?: Dispatch<SetStateAction<IMAGE_STATUS>>;
  comparedRetryCurrentImage?: (() => void) | null;
  onDrawDone?: (bbox: BBox2D) => void;
  isViewMode: boolean;
  onSelectObject?: (objectId: string) => void;
}

const PanoramaViewer = ({
  onNavigate,
  canNavigateForward = false,
  canNavigateBackward = false,
  canNavigateLeft = false,
  canNavigateRight = false,
  navigationDisabled = false,
  cameraPositions = [],
  currentFrameIndex = 0,
  onFrameChange = null,
  debugCameraPositions = false,
  currentSrc = null,
  currentImageStatus = IMAGE_STATUS.LOADING,
  setCurrentImageStatus,
  retryCurrentImage = null,
  activeObjectId = null,
  isLVMode = false,

  comparedCameraPositions = [],
  comparedCurrentSrc = null,
  comparedCurrentImageStatus = IMAGE_STATUS.LOADING,
  setComparedCurrentImageStatus,
  comparedRetryCurrentImage = null,
  onDrawDone = () => {},
  isViewMode,
  onSelectObject,
}: PanoramaViewerProps) => {
  // Refs
  const sceneRef = useRef<AFrameScene>(null);
  const skyRef = useRef<AFrameSky>(null);
  const secondSkyRef = useRef<AFrameSky>(null);
  const cameraRef = useRef<AFrameCamera>(null);
  const secondSceneRef = useRef<AFrameScene>(null);
  const secondCameraRef = useRef<AFrameCamera>(null);
  const drawElementRef = useRef<HTMLElement>(null);

  // State
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isSplitView, setIsSplitView] = useState<boolean>(false);
  const [loadedImageIndex, setLoadedImageIndex] = useState<number | false>(
    false,
  );
  const [comparedLoadedImageIndex, setComparedLoadedImageIndex] = useState<
    number | false
  >(false);
  const [shouldSkipCameraSetNextPosition, setShouldSkipCameraSetNextPosition] =
    useState<boolean>(false);
  const previousFrameIndex = usePrevious(currentFrameIndex);
  const { user } = useAuth();

  const [isDrawingMode, setIsDrawingMode] = useState<boolean>(false);

  // Custom hooks
  const { fovRef, setFov, setSecondFov } = useFOVManagement(
    cameraRef,
    secondCameraRef,
    isSplitView,
  );
  const { activeScene, setActiveScene } = useSceneInteraction(
    sceneRef,
    secondSceneRef,
    isSplitView,
  );
  useCameraSync(cameraRef, secondCameraRef, isSplitView, activeScene);

  const { imgWidth, imgHeight } = useImageLoading(
    skyRef,
    currentFrameIndex,
    setCurrentImageStatus,
    setLoadedImageIndex,
    isSplitView,
  );

  useImageLoading(
    secondSkyRef,
    currentFrameIndex,
    setComparedCurrentImageStatus || (() => {}),
    setComparedLoadedImageIndex,
    isSplitView,
  );

  // Zoom handlers for both scenes
  useZoomHandlers(sceneRef, setFov, fovRef, true);
  useZoomHandlers(
    secondSceneRef,
    (fov, x, y) => setSecondFov(fov, x, y, secondSceneRef),
    fovRef,
    isSplitView,
  );

  const { clearDrawing } = useDrawObject({
    isDrawingMode,
    scene: sceneRef.current,
    drawElement: drawElementRef.current,
    camera: cameraRef.current,
    imgWidth,
    imgHeight,
    onDone: onDrawDone,
  });

  useEffect(() => {
    if (isDrawingMode) {
      clearDrawing();
      setIsDrawingMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraPositions]);

  useEffect(() => {
    if (activeObjectId) {
      setShouldSkipCameraSetNextPosition(true);
    }
  }, [activeObjectId]);

  useEffect(() => {
    if (previousFrameIndex !== currentFrameIndex) {
      setShouldSkipCameraSetNextPosition(false);
    }
  }, [currentFrameIndex, previousFrameIndex]);

  // Helper function to transform camera coordinates
  const transformCameraPosition = useCallback(
    (
      position: [number, number, number],
      currentCameraPosition: [number, number, number],
      scaleFactor: number = 0.1,
      offset: [number, number, number] = [0, 0, 0],
    ): [number, number, number] => {
      // Calculate deltas
      const dx = position[0] - currentCameraPosition[0];
      const dy = position[1] - currentCameraPosition[1];
      const dz = position[2] - currentCameraPosition[2];

      // Use Swap X↔Z as base (was the blue option that looked relatively correct)
      // But try variations to get it exactly right
      const swapXZ: [number, number, number] = [
        dz * scaleFactor + offset[0],
        dy * scaleFactor + offset[1],
        dx * scaleFactor + offset[2],
      ];

      return swapXZ;
    },
    [],
  );

  const setCameraToNextPosition = useCallback(() => {
    setActiveScene("main");

    const camera = cameraRef.current;

    if (!camera) return;

    const currentRaw = cameraPositions[loadedImageIndex as number]?.position;
    const nextRaw = cameraPositions[(loadedImageIndex as number) + 1]?.position;
    if (!currentRaw || !nextRaw) return;

    const rawPosition = transformCameraPosition(
      nextRaw,
      currentRaw,
      0.5,
      [0, 0, 0],
    );

    const nextRelative = rotateY(rawPosition, 90);

    const dx = nextRelative[0];
    const dy = nextRelative[1];
    const dz = nextRelative[2];

    const yaw = Math.atan2(-dx, -dz) * (180 / Math.PI);
    const horizontalDistance = Math.sqrt(dx * dx + dz * dz);
    const pitch = -Math.atan2(dy, horizontalDistance) * (180 / Math.PI);

    const rotation = { x: pitch, y: yaw, z: 0 };

    camera.setAttribute(
      "rotation",
      `${rotation.x} ${rotation.y} ${rotation.z}`,
    );

    const controls = camera.components?.["custom-look-controls"];
    if (controls) {
      controls.pitchObject.rotation.x = (rotation.x * Math.PI) / 180;
      controls.yawObject.rotation.y = (rotation.y * Math.PI) / 180;
    }
  }, [
    setActiveScene,
    cameraPositions,
    loadedImageIndex,
    transformCameraPosition,
  ]);

  const handleZoomIn = useCallback(() => {
    const currentFov = fovRef.current || FOV;
    const newFov = Math.max(currentFov - 10, MIN_FOV);
    setFov(newFov);
    if (isSplitView) {
      setSecondFov(newFov, undefined, undefined, secondSceneRef);
    }
  }, [fovRef, setFov, isSplitView, setSecondFov]);

  const handleZoomOut = useCallback(() => {
    const currentFov = fovRef.current || FOV;
    const newFov = Math.min(currentFov + 10, MAX_FOV);
    setFov(newFov);
    if (isSplitView) {
      setSecondFov(newFov, undefined, undefined, secondSceneRef);
    }
  }, [fovRef, setFov, isSplitView, setSecondFov]);

  const toggleDrawingMode = useCallback(() => {
    if (user?.isGlobalAdmin) {
      clearDrawing();
      setIsDrawingMode((prev) => !prev);
    }
  }, [clearDrawing, user?.isGlobalAdmin]);

  const previousLoadedImageIndex = usePrevious(loadedImageIndex);
  useLayoutEffect(() => {
    if (
      loadedImageIndex !== null &&
      !shouldSkipCameraSetNextPosition &&
      loadedImageIndex !== previousLoadedImageIndex
    ) {
      setCameraToNextPosition();
    }
  }, [
    loadedImageIndex,
    setCameraToNextPosition,
    shouldSkipCameraSetNextPosition,
    previousLoadedImageIndex,
  ]);

  // Navigation handlers
  const handleNavigate = useCallback(
    (direction: NavigationDirection) => {
      if (onNavigate && !navigationDisabled) {
        onNavigate(direction);
      }
    },
    [onNavigate, navigationDisabled],
  );

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case "w":
        case "W":
          event.preventDefault();
          toggleDrawingMode();
          break;
        case "Escape":
          if (isDrawingMode) {
            event.preventDefault();
            clearDrawing();
            setIsDrawingMode(false);
          }
          break;

        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    canNavigateForward,
    clearDrawing,
    handleNavigate,
    isDrawingMode,
    toggleDrawingMode,
  ]);

  // Fullscreen handling
  const toggleFullscreen = () => {
    const element = sceneRef.current?.parentElement;
    if (!document.fullscreenElement && element) {
      const req =
        element.requestFullscreen ||
        (element as any).webkitRequestFullscreen ||
        (element as any).mozRequestFullScreen ||
        (element as any).msRequestFullscreen;

      if (req) {
        req
          .call(element)
          .then(() => {
            setIsFullscreen(true);
          })
          .catch((err: any) => {
            // eslint-disable-next-line no-console
            console.error("Error attempting to enable fullscreen:", err);
          });
      }
    } else if (document.fullscreenElement) {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Handle camera position click
  const handleCameraClick = useCallback(
    (frameIndex: number) => {
      console.log("handle camera click");
      if (onFrameChange && frameIndex !== currentFrameIndex) {
        onFrameChange(frameIndex);
      }
    },
    [currentFrameIndex, onFrameChange],
  );

  // Set up A-Frame click event listeners
  useEffect(() => {
    const scene = sceneRef.current;
    const secondScene = secondSceneRef.current;

    // Wait for scene to be loaded
    const setupClickHandlers = () => {
      const handleSceneClick = (event: Event) => {
        const target = event.target as HTMLElement;
        const frameIndex = target.getAttribute("data-frame-index");

        if (frameIndex !== null) {
          const index = parseInt(frameIndex, 10);
          if (index !== currentFrameIndex && onFrameChange) {
            handleCameraClick(index);
          }
        }
      };

      scene?.addEventListener("click", handleSceneClick);
      secondScene?.addEventListener("click", handleSceneClick);

      return () => {
        scene?.removeEventListener("click", handleSceneClick);
        secondScene?.removeEventListener("click", handleSceneClick);
      };
    };

    let cleanup = () => {};

    if (scene?.hasLoaded) {
      cleanup = setupClickHandlers();
    } else {
      const onLoaded = () => {
        cleanup = setupClickHandlers();
        scene?.removeEventListener("loaded", onLoaded);
        secondScene?.removeEventListener("loaded", onLoaded);
      };
      scene?.addEventListener("loaded", onLoaded);
      secondScene?.addEventListener("loaded", onLoaded);

      cleanup = () => {
        scene?.removeEventListener("loaded", onLoaded);
        secondScene?.removeEventListener("loaded", onLoaded);
      };
    }

    return cleanup;
  }, [
    cameraPositions,
    currentFrameIndex,
    handleCameraClick,
    onFrameChange,
    isSplitView,
  ]);

  useEffect(() => {
    if (sceneRef.current?.resize) {
      sceneRef.current.resize();
    }
    if (secondSceneRef.current?.resize) {
      secondSceneRef.current.resize();
    }
  }, [isSplitView]);

  return (
    <div className="w-full h-full relative">
      {retryCurrentImage && (
        <LoadingOverlay
          isSplitView={isSplitView}
          currentImageStatus={currentImageStatus}
          retryCurrentImage={retryCurrentImage}
          comparedCurrentImageStatus={comparedCurrentImageStatus}
          comparedRetryCurrentImage={comparedRetryCurrentImage}
        />
      )}
      {/* Main A-Frame Scene */}
      <PanoramaScene
        sceneRef={sceneRef}
        cameraRef={cameraRef}
        skyRef={skyRef}
        currentSrc={currentSrc}
        isSplitView={isSplitView}
        FOV={FOV}
        cameraPositions={cameraPositions}
        loadedImageIndex={loadedImageIndex}
        debugCameraPositions={debugCameraPositions}
        transformCameraPosition={transformCameraPosition}
        activeObjectId={activeObjectId}
        imageWidth={imgWidth}
        imageHeight={imgHeight}
        onSelectObject={onSelectObject}
      >
        <a-entity data-attr="test" ref={drawElementRef} />
      </PanoramaScene>
      {/* Second A-Frame Scene for Split View */}
      {isSplitView && (
        <PanoramaScene
          skyRef={secondSkyRef}
          sceneRef={secondSceneRef}
          cameraRef={secondCameraRef}
          currentSrc={comparedCurrentSrc}
          isSplitView={isSplitView}
          isSecondScene={true}
          FOV={FOV}
          cameraPositions={comparedCameraPositions}
          loadedImageIndex={comparedLoadedImageIndex}
          debugCameraPositions={debugCameraPositions}
          transformCameraPosition={transformCameraPosition}
          activeObjectId={activeObjectId}
          imageWidth={imgWidth}
          imageHeight={imgHeight}
          onSelectObject={onSelectObject}
        />
      )}
      <PanoramaControls
        isFullscreen={isFullscreen}
        isSplitView={isSplitView}
        canSplitView={!isLVMode && !isViewMode}
        toggleFullscreen={toggleFullscreen}
        setIsSplitView={setIsSplitView}
        setCameraToNextPosition={setCameraToNextPosition}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        isDrawingMode={isDrawingMode}
        toggleDrawingMode={toggleDrawingMode}
        canDraw={!isViewMode && user?.isGlobalAdmin}
        canResetView={!isViewMode}
      />
      <NavigationArrows
        canNavigateForward={canNavigateForward}
        canNavigateBackward={canNavigateBackward}
        canNavigateLeft={canNavigateLeft}
        canNavigateRight={canNavigateRight}
        navigationDisabled={navigationDisabled}
        handleNavigate={handleNavigate}
      />
    </div>
  );
};

export default memo(PanoramaViewer);
